export type TimePickerType = "minutes" | "seconds" | "hours" | "12hours";
export type Period = "AM" | "PM";

/**
 * @description regular expression to check for valid hour format (01-23)
 * @param {string} value - the value of the input
 * @returns {boolean} - the result of the test
 */
export function isValidHour(value: string): boolean {
  return /^(0[0-9]|1[0-9]|2[0-3])$/.test(value);
}

/**
 * @description - regular expression to check for valid 12 hour format (01-12)
 * @param {string} value - the value of the input
 * @returns {boolean} - the result of the test
 */
export function isValid12Hour(value: string): boolean {
  return /^(0[1-9]|1[0-2])$/.test(value);
}

/**
 * @description - regular expression to check for valid minute or second format (00-59)
 * @param {string} value - the value of the input
 * @returns {boolean} - the result of the test
 */
export function isValidMinuteOrSecond(value: string): boolean {
  return /^[0-5][0-9]$/.test(value);
}

type GetValidNumberConfig = { max: number; min?: number; loop?: boolean };

/**
 * @param {string} value - the value of the input
 * @param {GetValidNumberConfig} root0 - the configuration object
 * @param {number} root0.max - the maximum value
 * @param {number} root0.min - the minimum value
 * @param {boolean} root0.loop - the loop value
 * @returns {string} - the new value of the input
 * @description - this function ensures that the value of the input is a valid number
 * and within the specified range
 */
export function getValidNumber(
  value: string,
  { max, min = 0, loop = false }: GetValidNumberConfig,
): string {
  let numericValue = parseInt(value, 10);

  if (!isNaN(numericValue)) {
    if (!loop) {
      if (numericValue > max) {
        numericValue = max;
      }
      if (numericValue < min) {
        numericValue = min;
      }
    } else {
      if (numericValue > max) {
        numericValue = min;
      }
      if (numericValue < min) {
        numericValue = max;
      }
    }

    return numericValue.toString().padStart(2, "0");
  }

  return "00";
}

/**
 *
 * @param {string} value - the value of the input
 * @returns {string} - the new value of the input
 */
export function getValidHour(value: string): string {
  if (isValidHour(value)) {
    return value;
  }

  return getValidNumber(value, { max: 23 });
}

/**
 * @param {string} value - the value of the input
 * @returns {string} - the new value of the input
 */
export function getValid12Hour(value: string): string {
  if (isValid12Hour(value)) {
    return value;
  }

  return getValidNumber(value, { min: 1, max: 12 });
}

/**
 * @param {string} value - the value of the input
 * @returns {string} - the new value of the input
 */
export function getValidMinuteOrSecond(value: string): string {
  if (isValidMinuteOrSecond(value)) {
    return value;
  }

  return getValidNumber(value, { max: 59 });
}

type GetValidArrowNumberConfig = {
  min: number;
  max: number;
  step: number;
};

/**
 * @param {string} value - the value of the input
 * @param {GetValidArrowNumberConfig} root0 - the configuration object
 * @param {number} root0.min - the minimum value
 * @param {number} root0.max - the maximum value
 * @param {number} root0.step - the step value
 * @returns {string} - the new value of the input
 */
export function getValidArrowNumber(
  value: string,
  { min, max, step }: GetValidArrowNumberConfig,
): string {
  let numericValue = parseInt(value, 10);
  if (!isNaN(numericValue)) {
    numericValue += step;

    return getValidNumber(String(numericValue), { min, max, loop: true });
  }

  return "00";
}

/**
 * @param {string} value - the value of the input
 * @param {number} step - the step value
 * @returns {string} - the new value of the input
 */
export function getValidArrowHour(value: string, step: number): string {
  return getValidArrowNumber(value, { min: 0, max: 23, step });
}

/**
 * @param {string} value - the value of the input
 * @param {number} step - the step value
 * @returns {string} - the new value of the input
 */
export function getValidArrow12Hour(value: string, step: number): string {
  return getValidArrowNumber(value, { min: 1, max: 12, step });
}

/**
 * @param {string} value - the value of the input
 * @param {number} step - the step value
 * @returns {string} - the new value of the input
 */
export function getValidArrowMinuteOrSecond(
  value: string,
  step: number,
): string {
  return getValidArrowNumber(value, { min: 0, max: 59, step });
}

/**
 * @param {Date} date - the date object
 * @param {string} value - the value of the input
 * @returns {Date} - the new date object
 */
export function setMinutes(date: Date, value: string): Date {
  const minutes = getValidMinuteOrSecond(value);
  date.setMinutes(parseInt(minutes, 10));

  return date;
}

/**
 * @param {Date} date - the date object
 * @param {string} value - the value of the input
 * @returns {Date} - the new date object
 */
export function setSeconds(date: Date, value: string): Date {
  const seconds = getValidMinuteOrSecond(value);
  date.setSeconds(parseInt(seconds, 10));

  return date;
}

/**
 * @param {Date} date - the date object
 * @param {string} value - the value of the input
 * @param {Date} limit - the limit value
 * @returns {Date} - the new date object
 */
export function setHours(date: Date, value: string, limit?: Date): Date {
  const hours = getValidHour(value);
  if (limit) {
    const newDate = new Date(date);
    newDate.setHours(parseInt(hours));
    if (newDate > limit) {
      // Adjust the date to ensure it does not exceed the limit
      date.setHours(limit.getHours());

      return date;
    }
  }

  date.setHours(parseInt(hours, 10));

  return date;
}

/**
 *
 * @param {number} hours - the hour value
 * @returns {string} - the 12-hour value
 */
export function display12HourValue(hours: number): string {
  if (hours === 0 || hours === 12) {
    return "12";
  }
  if (hours >= 22) {
    return `${hours - 12}`;
  }
  if (hours % 12 > 9) {
    return `${hours}`;
  }

  return `0${hours % 12}`;
}

/**
 * @param {Date} date - the date object
 * @param {TimePickerType} type - the type of the input
 * @returns {string} - the value of the input
 */
export function getDateByType(date: Date, type: TimePickerType): string {
  switch (type) {
    case "minutes": {
      return getValidMinuteOrSecond(String(date?.getMinutes()));
    }
    case "seconds": {
      return getValidMinuteOrSecond(String(date?.getSeconds()));
    }
    case "hours": {
      return getValidHour(String(date?.getHours()));
    }
    case "12hours": {
      const hours = display12HourValue(date?.getHours());

      return getValid12Hour(String(hours));
    }

    default:
      return "00";
  }
}

/**
 * @param {string} value - the current value of the input
 * @param {number} step - the step value
 * @param {TimePickerType} type - the type of the input
 * @returns {string} - the new value of the input
 */
export function getArrowByType(
  value: string,
  step: number,
  type: TimePickerType,
): string {
  switch (type) {
    case "minutes":
      return getValidArrowMinuteOrSecond(value, step);
    case "seconds":
      return getValidArrowMinuteOrSecond(value, step);
    case "hours":
      return getValidArrowHour(value, step);
    case "12hours":
      return getValidArrow12Hour(value, step);
    default:
      return "00";
  }
}

/**
 * @description handles value change of 12-hour input
 * 12:00 PM is 12:00
 * 12:00 AM is 00:00
 * @param {number} hour - the hour value
 * @param {Period} period - the period value
 * @returns {number} - the 24-hour value
 */
export function convert12HourTo24Hour(hour: number, period: Period): number {
  if (period === "PM") {
    if (hour <= 11) {
      return hour + 12;
    } else {
      return hour;
    }
  } else if (period === "AM") {
    if (hour === 12) {
      return 0;
    }

    return hour;
  }

  return hour;
}

/**
 * @description sets the hours of the date object
 * @param {Date} date - the date object
 * @param {string} value - the value of the input
 * @param {Period} period - the period value
 * @param {Date} limit - the limit value
 * @returns {Date} - the new date object
 */
export function set12Hours(
  date: Date,
  value: string,
  period: Period,
  limit?: Date,
): Date {
  // Convert the 12-hour value to 24-hour format
  const hours = parseInt(getValid12Hour(value), 10);
  const convertedHours = convert12HourTo24Hour(hours, period);
  // Check if the new hour value would result in a date greater than the limit
  if (limit) {
    const newDate = new Date(date);
    newDate.setHours(convertedHours);
    if (newDate > limit) {
      const newConvertedHours = convert12HourTo24Hour(limit.getHours(), period);
      // Adjust the date to ensure it does not exceed the limit
      date.setHours(convert12HourTo24Hour(newConvertedHours, period));

      return date;
    }
  }
  // Set the hours if the date does not exceed the limit
  date.setHours(convertedHours);

  return date;
}

type TsetDateByType = {
  date: Date;
  value: string;
  type: TimePickerType;
  period?: Period;
  limit?: Date | undefined;
};

/**
 * @param {Date} date - the date object
 * @param {string} value - the value of the input
 * @param {TimePickerType} type - the type of the input
 * @param {Period} period - the period value
 * @param {Date} limit - the limit value
 * @returns {Date} - the new date object
 */
export function setDateByType({
  date,
  value,
  type,
  period,
  limit,
}: TsetDateByType): Date {
  switch (type) {
    case "minutes":
      return setMinutes(date, value);
    case "seconds":
      return setSeconds(date, value);
    case "hours":
      return setHours(date, value, limit);
    case "12hours": {
      if (!period) {
        return date;
      }

      return set12Hours(date, value, period, limit);
    }
    default:
      return date;
  }
}
